import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import ClaimDiagram from '../components/ClaimDiagram';
import './PatentDetail.css';

function PatentDetail() {
  const { patentId } = useParams();
  const [patent, setPatent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchPatentData = async () => {
      setLoading(true);
      setError(null);

      try {
        const patentResponse = await axios.get(`${config.apiBaseUrl}/patents/${patentId}`);
        setPatent(patentResponse.data);
      } catch (err) {
        setError('Error loading patent details. Please try again.');
        console.error('Patent detail error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPatentData();
  }, [patentId]);

  if (loading) return <div className="loading">Loading patent details...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!patent) return <div className="error">Patent not found</div>;

  const renderTags = (items, type) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="tag-list">
        {items.map((item, idx) => (
          <span key={idx} className={`tag ${type}`}>{item}</span>
        ))}
      </div>
    );
  };

  const renderFeatureTable = (features) => {
    if (!features || features.length === 0) return null;

    return (
      <table className="features-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Feature</th>
            <th>Type</th>
            <th>Optional</th>
            <th>Characteristics</th>
          </tr>
        </thead>
        <tbody>
          {features.map(feature => (
            <tr key={feature.feature_id}>
              <td className="feature-id">{feature.feature_id}</td>
              <td className="feature-label">{feature.label}</td>
              <td className="feature-type">{feature.type}</td>
              <td className="feature-optional">{feature.optional ? 'Yes' : 'No'}</td>
              <td className="feature-chars">
                {feature.characteristics && feature.characteristics.length > 0 ? (
                  <ul>
                    {feature.characteristics.map((char, idx) => (
                      <li key={idx}>{char.text}</li>
                    ))}
                  </ul>
                ) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="patent-detail">
      <div className="patent-header">
        <h1>{patent.title}</h1>
        <div className="patent-id-section">
          <span className="patent-id-label">US Patent Number:</span>
          <a 
            href={`https://image-ppubs.uspto.gov/dirsearch-public/print/downloadPdf/${patentId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="patent-pdf-link"
          >
            {patentId}
          </a>
        </div>
      </div>

      <div className="patent-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'claims' ? 'active' : ''}`}
          onClick={() => setActiveTab('claims')}
        >
          Claims Analysis
        </button>
        <button
          className={`tab ${activeTab === 'raw' ? 'active' : ''}`}
          onClick={() => setActiveTab('raw')}
        >
          Raw Data
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="patent-content">
          <section className="section">
            <h2>Plain English Summary</h2>
            <p className="summary">{patent.plain_english_summary}</p>
          </section>

          <section className="section">
            <h2>Technical Description</h2>
            <p>{patent.technical_description}</p>
          </section>

          <section className="section">
            <h2>Domains</h2>
            {renderTags(patent.domains, 'domain')}
          </section>

          <section className="section">
            <h2>Problem Space</h2>
            {renderTags(patent.problem_space, 'problem')}
          </section>

          <section className="section">
            <h2>Solution Space</h2>
            {renderTags(patent.solution_space, 'solution')}
          </section>

          <section className="section">
            <h2>IPC Codes</h2>
            {renderTags(patent.ipc_codes, 'ipc')}
          </section>

          {patent.entry_metadata && patent.entry_metadata.summary && (
            <section className="section">
              <h2>Detailed Summary</h2>
              <div className="summary-sections">
                {Object.entries(patent.entry_metadata.summary).map(([key, value]) => (
                  <div key={key} className="summary-item">
                    <h3>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                    <p>{value}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {activeTab === 'claims' && (
        <div className="patent-content">
          {patent.claim_analysis && (
            <>
              <section className="section">
                <h2>Broadest Claim (Claim {patent.claim_analysis.broadest_claim_id})</h2>
                <div className="claim-text">
                  {patent.claim_analysis.broadest_claim_text}
                </div>
              </section>

              <section className="section">
                <h2>Claim Visualization</h2>
                <ClaimDiagram claim={patent.claim_analysis} />
              </section>

              {patent.claim_analysis.features && (
                <section className="section">
                  <h2>Feature Table</h2>
                  {renderFeatureTable(patent.claim_analysis.features)}
                </section>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'raw' && patent.raw_portfolio && (
        <div className="patent-content">
          <section className="section metadata-section">
            <h2>Patent Metadata</h2>
            <div className="metadata-grid">
              <div className="metadata-item">
                <strong className="metadata-key">Publication Number:</strong>
                <span className="metadata-value">{patent.raw_portfolio.publication_number}</span>
              </div>
              <div className="metadata-item">
                <strong className="metadata-key">Publication Date:</strong>
                <span className="metadata-value">{patent.raw_portfolio.publication_date}</span>
              </div>
              <div className="metadata-item">
                <strong className="metadata-key">Filing Date:</strong>
                <span className="metadata-value">{patent.raw_portfolio.filing_date}</span>
              </div>
              <div className="metadata-item">
                <strong className="metadata-key">Grant Date:</strong>
                <span className="metadata-value">{patent.raw_portfolio.grant_date}</span>
              </div>
              <div className="metadata-item">
                <strong className="metadata-key">Art Unit:</strong>
                <span className="metadata-value">{patent.raw_portfolio.groupArtUnitNumber}</span>
              </div>
              <div className="metadata-item">
                <strong className="metadata-key">Examiner:</strong>
                <span className="metadata-value">{patent.raw_portfolio.examinerNameText}</span>
              </div>
              <div className="metadata-item">
                <strong className="metadata-key">Class/Subclass:</strong>
                <span className="metadata-value">{patent.raw_portfolio.class}/{patent.raw_portfolio.subclass}</span>
              </div>
              {patent.raw_portfolio.cpc_main && (
                <div className="metadata-item">
                  <strong className="metadata-key">CPC Classifications:</strong>
                  <span className="metadata-value">{patent.raw_portfolio.cpc_main.join(', ')}</span>
                </div>
              )}
              {patent.raw_portfolio.inventors && (
                <div className="metadata-item metadata-item-full">
                  <strong className="metadata-key">Inventors:</strong>
                  <span className="metadata-value">
                    {patent.raw_portfolio.inventors.map(inv => inv.inventorNameText).join('; ')}
                  </span>
                </div>
              )}
            </div>
          </section>

          <section className="section">
            <h2>Title</h2>
            <p className="formatted-text">{patent.raw_portfolio.title}</p>
          </section>

          <section className="section">
            <h2>Abstract</h2>
            <p className="formatted-text">{patent.raw_portfolio.abstract.replace(/\\n/g, '\n')}</p>
          </section>

          <section className="section">
            <h2>Claims</h2>
            <pre className="formatted-text">{patent.raw_portfolio.claims.replace(/\\n/g, '\n')}</pre>
          </section>

          <section className="section">
            <h2>Description</h2>
            <pre className="formatted-text">{patent.raw_portfolio.description.replace(/\\n/g, '\n')}</pre>
          </section>
        </div>
      )}
    </div>
  );
}

export default PatentDetail;
